'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getProfile, getSessions, computeStreak, averageRating } from '@/lib/storage';
import type { UserProfile, InterventionSession } from '@/lib/storage';
import { getContextLabel, getStyleLabel } from '@/lib/content';
import AppBottomNav from '@/components/AppBottomNav';
import PanicButton from './PanicButton';
import RecentSessions from './RecentSessions';
import QuickStats from './QuickStats';
import InstallPrompt from '@/components/InstallPrompt';

export default function HomeShell() {
  const router = useRouter();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [sessions, setSessions] = useState<InterventionSession[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [offlineReady, setOfflineReady] = useState(false);

  useEffect(() => {
    const p = getProfile();
    if (!p?.onboardingComplete) {
      router.replace('/onboarding-screen');
      return;
    }
    setProfile(p);
    setSessions(getSessions());
    setLoaded(true);
  }, [router]);

  useEffect(() => {
    // Set initial online state
    setIsOnline(navigator.onLine);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check if service worker is controlling the page (offline-ready)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then(() => {
        setOfflineReady(true);
      }).catch(() => {});
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!loaded) return null;

  const streak = computeStreak(sessions);
  const avg = averageRating(sessions);

  return (
    <div className="min-h-dvh bg-[hsl(var(--background))] flex flex-col pb-20">
      {/* Header */}
      <div className="safe-top px-5 pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] font-medium tracking-wide uppercase">
              RelationalACT
            </p>
            {profile && (
              <p className="text-sm text-[hsl(var(--foreground))] mt-0.5">
                {getContextLabel(profile.relationshipContext)} · {getStyleLabel(profile.attachmentStyle)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Offline-ready / online status badge */}
            {offlineReady && (
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-all ${
                  isOnline
                    ? 'bg-[hsl(142_70%_45%/0.12)] border-[hsl(142_70%_45%/0.3)] text-[hsl(142_70%_55%)]'
                    : 'bg-[hsl(38_90%_62%/0.12)] border-[hsl(38_90%_62%/0.3)] text-[hsl(38_90%_62%)]'
                }`}
                title={isOnline ? 'Połączono — działa też offline' : 'Tryb offline'}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isOnline ? 'bg-[hsl(142_70%_55%)]' : 'bg-[hsl(38_90%_62%)]'
                  }`}
                />
                {isOnline ? 'Gotowe offline' : 'Offline'}
              </div>
            )}
            {streak > 0 && (
              <div className="flex items-center gap-1.5 bg-[hsl(38_90%_62%/0.12)] border border-[hsl(38_90%_62%/0.25)] px-3 py-1.5 rounded-full">
                <span className="text-base">🔥</span>
                <span className="text-sm font-semibold text-[hsl(38_90%_62%)]">{streak}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Offline banner (shown when actually offline) */}
      {!isOnline && (
        <div className="mx-5 mb-2 px-4 py-2.5 rounded-xl bg-[hsl(38_90%_62%/0.1)] border border-[hsl(38_90%_62%/0.2)] flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[hsl(38_90%_62%)] flex-shrink-0">
            <path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0119 12.55M5 12.55a10.94 10.94 0 015.17-2.39M10.71 5.05A16 16 0 0122.56 9M1.42 9a15.91 15.91 0 014.7-2.88M8.53 16.11a6 6 0 016.95 0M12 20h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <p className="text-xs text-[hsl(38_90%_62%)]">
            Brak połączenia — aplikacja działa w trybie offline
          </p>
        </div>
      )}

      {/* Panic button area */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-6">
        <div className="mb-4 text-center">
          <h2 className="text-lg font-semibold text-[hsl(var(--foreground))]">
            Jak się teraz czujesz?
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Naciśnij i przytrzymaj, aby rozpocząć interwencję
          </p>
        </div>

        <PanicButton onActivate={() => router.push('/intervention-screen')} />

        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-5 text-center">
          Zajmie to maksymalnie 2 minuty
        </p>
      </div>

      {/* Quick stats */}
      <div className="px-5 mb-4">
        <QuickStats sessions={sessions} streak={streak} avg={avg} />
      </div>

      {/* Recent sessions */}
      <div className="px-5">
        <RecentSessions sessions={sessions.slice(0, 3)} />
      </div>

      <AppBottomNav active="home" />

      {/* PWA Install Prompt */}
      <InstallPrompt />
    </div>
  );
}